/**
 * Module utility functions for the course editor
 */
import { Icon } from '../../../ui';
import { TASK_TYPE } from '../../../utils/constants';
import { MODULE_CAPABILITIES } from '../moduleConfig';
import { PROVIDER_TYPES } from '../../../helpers/media';

/**
 * Get the icon component for a module type
 * @param {string} moduleType - The module type name
 * @returns {React.Component} - The icon component
 */
export const getModuleIconComponent = (moduleType) => {
  const iconName = MODULE_CAPABILITIES[moduleType]?.icon;
  return iconName ? Icon[iconName] : Icon.ViewModule;
};

/**
 * Get the display title for a module
 * @param {Object} module - The module data
 * @returns {string} - The module title
 */
export const getModuleTitle = (module) => {
  const moduleType = module?.type?.name;
  if (module?.data?.title) return module.data.title;
  if (module?.data?.name) return module.data.name;
  if (module?.data?.question) return module.data.question;
  return `${moduleType} Module`;
};

/**
 * Check if a module is empty (has no content)
 * Used to show empty state placeholders in edit mode
 *
 * @param {Object} module - The module data
 * @returns {boolean} - True if the module is empty
 */
export const isModuleEmpty = (module) => {
  const moduleType = module?.type?.name;
  const data = module?.data;

  // Discussion, Webinar, and Diploma always show UI regardless of content
  if (['discussion', 'webinar', 'diploma'].includes(moduleType)) return false;

  switch (moduleType) {
    case 'image':
      return !data?.content?.[0]?.url;

    case 'text':
      return !data?.content;

    case 'video':
      // Check for uploaded video or external provider
      return !data?.content?.[0]?.url && !data?.params?.external?.provider;

    case 'audio': {
      // Check for uploaded audio
      if (data?.content?.[0]?.url) return false;
      // Check for external provider (Spotify, etc.)
      const external = data?.params?.external;
      if (external?.provider) return false;
      // Check for iframe embed in external.id
      if (typeof external?.id === 'string' && external.id.includes('<iframe')) return false;
      return true;
    }

    case 'download':
    case 'slideshow':
      return !data?.content?.length;

    case 'embed': {
      // Check sources array
      const source = data?.sources?.[0];
      if (source?.data || source?.provider) return false;
      // Check params provider
      if (data?.params?.provider) return false;
      return true;
    }

    case 'task': {
      // Task is empty if no task data configured
      const task = data?.params?.task;
      const itemsLength = task.caption.items?.length;

      if (!task) return true;

      // Has a task ID means it's been saved but does not mean it has been fully configured
      // if (task.taskId) return false;

      // main caption can be empty - aparently some clients use a text module to explain the task instead of using a main caption
      if (!(task.type === TASK_TYPE.OPEN || task.type === TASK_TYPE.FILE) && !itemsLength) return true;

      if (task.type === TASK_TYPE.SINGLE || task.type === TASK_TYPE.MULTIPLE) {
        if (!task.caption?.main || !task.caption?.items[0].length) return true;
      }

      return false;
    }

    default:
      return false;
  }
};

/**
 * Check if a module has unsupported content
 * Used to show unsupported state placeholders in edit mode
 * This is different from empty - the module HAS content but we can't display it
 *
 * @param {Object} module - The module data
 * @returns {boolean} - True if the module has unsupported content
 */
export const isModuleContentUnsupported = (module) => {
  const moduleType = module?.type?.name;
  const data = module?.data;

  switch (moduleType) {
    case 'audio': {
      // Provider LINK (raw link) is not supported for audio playback
      // Only Spotify and SoundCloud with iframe are supported
      const external = data?.params?.external;
      if (external?.provider === PROVIDER_TYPES.LINK) return true;
      return false;
    }

    default:
      return false;
  }
};
